/**
 * WeaveIn.ai Platform - Enrollment Detail Component (Tailwind CSS)
 * Migrated from Material-UI to Tailwind CSS with Radix UI primitives
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft as BackIcon,
  Pencil as EditIcon,
  Plus as AddIcon,
  FileText as DocumentIcon,
  StickyNote as NoteIcon,
  Flag as GoalIcon,
  Phone as PhoneIcon,
  Mail as EmailIcon,
  MessageSquare as SmsIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ScheduleIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  ArrowRight as NextStepIcon,
  Loader2,
  Trash2,
  Eye as EyeIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  PenTool as PenToolIcon,
  Printer as PrinterIcon,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DocumentUploadDialog } from '@/components/documents/DocumentUploadDialog';
import OutreachEncounterPanel from '@/components/outreach/OutreachEncounterPanel';
import { CollapsibleCard } from '@/components/communications/insights/CollapsibleCard';
import { DocumentRequirements } from '@/components/documents/DocumentRequirements';
import { RequestSignaturePage } from '@/components/signatures/RequestSignaturePage';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PrintPacketDialog from '@/components/members/print/PrintPacketDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import useEnrollments, {
  useEnrollmentActivities,
  useEnrollmentDocuments,
  useEnrollmentNotes,
  useEnrollmentGoals,
} from '@/hooks/useEnrollments';
import useCommunicationResponses from '@/hooks/useCommunicationResponses';
import { Enrollment, ActivityCreate, NoteCreate, GoalCreate, ActivityStatus } from '@/services/api/enrollments';
import { membersAPI, Member } from '@/services/api/members';
import { workflowsAPI, WorkflowTemplate } from '@/services/api/workflows';
import { useEnrollmentWorkflows } from '@/hooks/useWorkflowInstance';
import {
  MemberWorkflowProgress,
  FormNavigationContext,
  CommunicationNavigationContext,
  DocumentNavigationContext,
} from '../workflows/MemberWorkflowProgress';
import { MemberPhoneInterface } from '../communications/MemberPhoneInterface';
import { MemberMessageComposer } from '../communications/MemberMessageComposer';
import MemberActivityFeed from '../members/MemberActivityFeed';
import { ResponseList } from '../communications/ResponseList';
import TaskList from '../tasks/TaskList';
import { FormLibraryNew } from '../forms/FormLibraryNew';
import { config } from '@/src/config';
import { formatDateOnly, formatDate, formatDateInTimezone } from '@/utils/dateTime';
import { useAuth } from '@/contexts/AuthContext';

import { useAppTranslation } from '@/contexts/TranslationContext';

interface EnrollmentDetailProps {
  enrollmentId?: number;
  onNavigate?: (tab: string, enrollmentId?: number) => void;
  onBack?: () => void;
  /** When set, component starts on this tab and hides its own header, stats
   *  cards, and tab bar — only the selected tab's content is rendered.
   *  Used by the Members page sidebar to embed individual sections. */
  defaultTab?: string;
  /** When true, hides the header (back btn, title, stats) and the tab bar.
   *  The tab content area is still rendered for whichever tab is active. */
  hideChrome?: boolean;
}

export const EnrollmentDetailNew: React.FC<EnrollmentDetailProps> = ({
  enrollmentId,
  onNavigate,
  onBack,
  defaultTab,
  hideChrome = false,
}) => {
  const { t } = useAppTranslation();
  const { preferences } = useAuth();
  const userTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  const [printOpen, setPrintOpen] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  const { getEnrollment, updateEnrollment } = useEnrollments({ autoFetch: false });
  const activities = useEnrollmentActivities(enrollmentId || 0);
  const documents = useEnrollmentDocuments(enrollmentId || 0);
  const notes = useEnrollmentNotes(enrollmentId || 0);
  const goals = useEnrollmentGoals(enrollmentId || 0);

  // Communication responses for this enrollment
  const {
    responses: communicationResponses,
    loading: responsesLoading,
    error: responsesError,
    unreadCount: responsesUnreadCount,
    refetch: refetchResponses,
    markAsRead: markResponseAsRead,
    updateFollowUp: updateResponseFollowUp
  } = useCommunicationResponses({
    enrollmentId: enrollmentId || undefined,
    autoFetch: !!enrollmentId
  });

  // Workflow instances for this enrollment
  const {
    instances: workflowInstances,
    templates: availableWorkflowTemplates,
    loading: workflowInstancesLoading,
    error: workflowInstancesError,
    createInstance: createWorkflowInstance,
    refetch: refetchWorkflowInstances,
  } = useEnrollmentWorkflows(enrollmentId, enrollment?.program_id);

  // Dialog states
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<number | null>(null);
  const [signatureRequestOpen, setSignatureRequestOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [communicationDialogOpen, setCommunicationDialogOpen] = useState(false);

  // Form data
  const [activityData, setActivityData] = useState<Partial<ActivityCreate>>({
    activity_type: 'outreach',
    activity_date: new Date().toISOString().split('T')[0],
    status: 'scheduled',
  });

  const [noteData, setNoteData] = useState<Partial<NoteCreate>>({
    note_text: '',
    note_type: 'general',
  });

  const [goalData, setGoalData] = useState<Partial<GoalCreate>>({
    goal_text: '',
    goal_category: 'health',
    status: 'active',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCommunicationMethod, setSelectedCommunicationMethod] = useState<'email' | 'sms' | 'phone'>('email');

  // Workflow navigation context - tracks when user navigates from workflow step to another tab
  interface WorkflowNavigationContext {
    workflowInstanceId: number;
    workflowStepId: number;
    workflowStepInstanceId: number;
    stepName: string;
    returnTab: string;
    formTemplateId?: number;
    communicationTemplateId?: number;
  }
  const [workflowNavContext, setWorkflowNavContext] = useState<WorkflowNavigationContext | null>(null);

  // Message composer: which channel is open. The composer itself (templates,
  // sender identity, merge fields, attachments, send) is MemberMessageComposer.
  const [showMessageComposer, setShowMessageComposer] = useState(false);

  // Sent messages history state
  const [sentMessages, setSentMessages] = useState<any[]>([]);
  const [sentMessagesLoading, setSentMessagesLoading] = useState(false);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    enrollment_status: '',
    enrollment_priority: '',
    assigned_to: '',
    enrollment_date: '',
    start_date: '',
    end_date: '',
    notes: '',
  });

  useEffect(() => {
    const fetchEnrollment = async () => {
      if (!enrollmentId) return;

      setLoading(true);
      try {
        const enrollmentData = await getEnrollment(enrollmentId);
        if (enrollmentData) {
          setEnrollment(enrollmentData);

          if (enrollmentData.member_id) {
            const memberData = await membersAPI.getMember(enrollmentData.member_id);
            setMember(memberData);
          }

          if (enrollmentData.program_id) {
            const workflowData = await workflowsAPI.listWorkflowTemplates({
              program_id: enrollmentData.program_id,
              is_active: true,
            });
            setWorkflows(workflowData.workflows || []);
          }
        }
      } catch (error) {
        console.error('Error fetching enrollment details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollment();
  }, [enrollmentId]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        if (!cboId) return;

        const response = await fetch(`${config.weaveinApi.baseUrl}/api/cbos/${cboId}/users?staff_only=true`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAvailableUsers(data || []);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Fetch templates and sent messages when conversations tab is opened
  useEffect(() => {
    if (activeTab === 'conversations') {
      fetchSentMessages();
    }
  }, [activeTab, member?.id]);

  const handleOpenEditModal = () => {
    if (!enrollment) return;

    setEditFormData({
      enrollment_status: enrollment.enrollment_status || '',
      enrollment_priority: enrollment.enrollment_priority || '',
      assigned_to: enrollment.assigned_to?.toString() || '',
      enrollment_date: enrollment.enrollment_date?.split('T')[0] || '',
      start_date: enrollment.start_date?.split('T')[0] || '',
      end_date: enrollment.end_date?.split('T')[0] || '',
      notes: enrollment.notes || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEnrollment = async () => {
    if (!enrollmentId) return;

    try {
      const updateData: any = {
        status: editFormData.enrollment_status,
        priority_level: editFormData.enrollment_priority,
        enrollment_date: editFormData.enrollment_date || undefined,
        start_date: editFormData.start_date || undefined,
        end_date: editFormData.end_date || undefined,
        notes: editFormData.notes || undefined,
      };

      if (editFormData.assigned_to) {
        updateData.assigned_to = parseInt(editFormData.assigned_to);
      }

      const updated = await updateEnrollment(enrollmentId, updateData);

      if (updated) {
        setEnrollment(updated);
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('Error updating enrollment:', error);
      alert('Failed to update enrollment');
    }
  };

  const handleCreateActivity = async () => {
    if (!enrollmentId) return;

    try {
      const success = await activities.createActivity({
        ...activityData,
        enrollment_id: enrollmentId,
      } as ActivityCreate);

      if (success) {
        setActivityDialogOpen(false);
        setActivityData({
          activity_type: 'outreach',
          activity_date: new Date().toISOString().split('T')[0],
          status: 'scheduled',
        });
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      alert('Failed to create activity');
    }
  };

  const handleCreateNote = async () => {
    if (!enrollmentId) return;

    try {
      const created = await notes.createNote({
        ...noteData,
        enrollment_id: enrollmentId,
      } as NoteCreate);

      if (created) {
        setNoteDialogOpen(false);
        setNoteData({
          note_text: '',
          note_type: 'general',
        });
      }
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Failed to create note');
    }
  };

  const handleCreateGoal = async () => {
    if (!enrollmentId) return;

    try {
      const created = await goals.createGoal({
        ...goalData,
        enrollment_id: enrollmentId,
      } as GoalCreate);

      if (created) {
        setGoalDialogOpen(false);
        setGoalData({
          goal_text: '',
          goal_category: 'health',
          status: 'active',
        });
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      alert('Failed to create goal');
    }
  };

  const handleUploadDocument = async () => {
    if (!enrollmentId || !selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    try {
      const categorizeDocument = (fileName: string): string => {
        const name = fileName.toLowerCase();
        if (name.includes('consent')) return 'Consent Form';
        if (name.includes('insurance') || name.includes('card')) return 'Insurance Card';
        if (name.includes('id')) return 'Identification';
        if (name.includes('referral')) return 'Referral Document';
        if (name.includes('assessment')) return 'Assessment';
        if (name.includes('care') || name.includes('plan')) return 'Care Plan';
        return 'Other Document';
      };

      const documentType = categorizeDocument(selectedFile.name);

      const created = await documents.createDocument({
        enrollment_id: enrollmentId,
        document_type: documentType,
        file_name: selectedFile.name,
        file_path: `/uploads/enrollments/${enrollmentId}/${selectedFile.name}`,
        file_size: selectedFile.size,
        mime_type: selectedFile.type,
      });

      if (created) {
        setDocumentDialogOpen(false);
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    }
  };

  // Fetch sent messages history
  const fetchSentMessages = async () => {
    if (!member?.id) return;

    setSentMessagesLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (!cboId) return;

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/staff/member-communications/member/${member.id}/history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter to only show messages for this enrollment
        const enrollmentMessages = enrollmentId
          ? data.filter((msg: any) => msg.enrollment_id === enrollmentId || !msg.enrollment_id)
          : data;
        setSentMessages(enrollmentMessages.slice(0, 20)); // Show last 20 messages
      }
    } catch (error) {
      console.error('Error fetching sent messages:', error);
    } finally {
      setSentMessagesLoading(false);
    }
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'default';
      case 'pending':
      case 'scheduled':
        return 'secondary';
      case 'on_hold':
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getPriorityVariant = (priority: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'default';
    }
  };

  if (!enrollmentId) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-semibold mb-2">{t('enrollments.no_enrollment_selected', 'No enrollment selected')}</h2>
        <p className="text-muted-foreground">{t('enrollments.please_select_an_enrollment_to', 'Please select an enrollment to view details')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm text-muted-foreground">{t('enrollments.loading_enrollment_details', 'Loading enrollment details...')}</p>
        </div>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="p-8 text-center">
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            <p className="font-semibold mb-1">{t('enrollments.enrollment_not_found', 'Enrollment not found')}</p>
            <p className="text-sm">{t('enrollments.the_requested_enrollment_could_not', 'The requested enrollment could not be found')}</p>
          </AlertDescription>
        </Alert>
        {onBack && (
          <Button onClick={onBack}>
            <BackIcon className="h-4 w-4 mr-2" />
            {t('enrollments.go_back', 'Go Back')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={hideChrome ? '' : 'p-4 md:p-6 max-w-[1400px] mx-auto'}>
      {/* Header — hidden when embedded in the Members page sidebar */}
      {!hideChrome && (
      <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <p className="text-[13px] text-ink-500">
          {enrollment.member_name || `Member #${enrollment.member_id}`} - {enrollment.program_name || `Program #${enrollment.program_id}`}
        </p>
        <div className="flex gap-2 flex-wrap">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <BackIcon className="h-4 w-4 mr-2" />
              {t('enrollments.back_to_list', 'Back to List')}
            </Button>
          )}
          <Button variant="outline" onClick={() => setPrintOpen(true)}>
            <PrinterIcon className="h-4 w-4 mr-2" />
            {t('printPacket.button', 'Print')}
          </Button>
          <Button onClick={handleOpenEditModal}>
            <EditIcon className="h-4 w-4 mr-2" />
            {t('enrollments.edit_enrollment', 'Edit Enrollment')}
          </Button>
        </div>
      </div>
      )}
      {printOpen && (
        <PrintPacketDialog
          open={printOpen}
          onClose={() => setPrintOpen(false)}
          memberId={enrollment.member_id}
          enrollmentId={enrollment.id}
        />
      )}

      {/* Overview Cards — hidden when embedded */}
      {!hideChrome && (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">
              {t('enrollments.status', 'Status')}
            </p>
            <Badge variant={getStatusVariant(enrollment.enrollment_status)}>
              {enrollment.enrollment_status}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">
              {t('enrollments.priority', 'Priority')}
            </p>
            <Badge variant={getPriorityVariant(enrollment.enrollment_priority || 'medium')}>
              {enrollment.enrollment_priority || 'Medium'}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">
              {t('enrollments.progress', 'Progress')}
            </p>
            <div className="flex items-center gap-2">
              <Progress value={enrollment.progress_percentage || 0} className="flex-1" />
              <span className="text-sm font-bold">{enrollment.progress_percentage || 0}%</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-2">
              {t('enrollments.activities_completed', 'Activities Completed')}
            </p>
            <p className="text-xl font-semibold">
              {enrollment.completed_activities || 0} / {enrollment.total_required_activities || 0}
            </p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Tab bar — hidden when embedded in Members page sidebar */}
        {!hideChrome && (
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
          <TabsList className="w-max min-w-full justify-start flex-nowrap">
            <TabsTrigger value="overview" className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              {t('enrollments.overview', 'Overview')}
            </TabsTrigger>
            <TabsTrigger value="activities" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Activities</span>
              <span className="sm:hidden">Activity</span>
              {(activities?.activities?.length || 0) > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {activities?.activities?.length || 0}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Documents</span>
              <span className="sm:hidden">Docs</span>
              {(documents?.documents?.length || 0) > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {documents?.documents?.length || 0}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Tasks</span>
              <span className="sm:hidden">Tasks</span>
              {(enrollment?.outstanding_tasks_count || 0) > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {enrollment?.outstanding_tasks_count || 0}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="conversations" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">Conversations &amp; Outreach</span>
              <span className="sm:hidden">Chat</span>
              {responsesUnreadCount > 0 && (
                <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                  {responsesUnreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="forms" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <span className="hidden sm:inline">{t('enrollments.forms', 'Forms')}</span>
              <span className="sm:hidden">{t('enrollments.forms', 'Forms')}</span>
            </TabsTrigger>
          </TabsList>
        </div>
        )}

        {/* Tab Content - Overview */}
        <TabsContent value="overview" className="mt-6">
          <div className="space-y-4">
            {/* Member Information */}
            {member && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('enrollments.member_information', 'Member Information')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.full_name', 'Full Name')}</p>
                      <p className="text-sm">{member.first_name} {member.last_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.member_number', 'Member Number')}</p>
                      <p className="text-sm">{member.member_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.date_of_birth', 'Date of Birth')}</p>
                      <p className="text-sm">{formatDateOnly(member.dob)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.gender', 'Gender')}</p>
                      <p className="text-sm">{member.gender || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.preferred_language', 'Preferred Language')}</p>
                      <p className="text-sm">{member.language || member.preferred_language || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.race', 'Race')}</p>
                      <p className="text-sm">{member.race || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.ethnicity', 'Ethnicity')}</p>
                      <p className="text-sm">{member.ethnicity || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Information */}
            {member && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('enrollments.contact_information', 'Contact Information')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.email', 'Email')}</p>
                      <p className="text-sm">{member.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.phone', 'Phone')}</p>
                      <p className="text-sm">{member.phone_no || member.phone_number || '-'}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.street_address', 'Street Address')}</p>
                      <p className="text-sm">{member.street_address || member.address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.city', 'City')}</p>
                      <p className="text-sm">{member.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.state', 'State')}</p>
                      <p className="text-sm">{member.state || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.zip_code', 'ZIP Code')}</p>
                      <p className="text-sm">{member.zip_code || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insurance Information */}
            {member && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('enrollments.insurance_information', 'Insurance Information')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.medicaid_id', 'Medicaid ID')}</p>
                      <p className="text-sm">{member.medicaid_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.medicare_id', 'Medicare ID')}</p>
                      <p className="text-sm">{member.medicare_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.commercial_insurance_id', 'Commercial Insurance ID')}</p>
                      <p className="text-sm">{member.commercial_insurance_id || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{t('enrollments.patient_id', 'Patient ID')}</p>
                      <p className="text-sm">{member.patient_id || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enrollment Details */}
            <Card>
              <CardHeader>
                <CardTitle>{t('enrollments.enrollment_details_1', 'Enrollment Details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('enrollments.enrollment_date', 'Enrollment Date')}</p>
                    <p className="text-sm">{enrollment.enrollment_date ? formatDateOnly(enrollment.enrollment_date) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('enrollments.start_date', 'Start Date')}</p>
                    <p className="text-sm">{enrollment.start_date ? formatDateOnly(enrollment.start_date) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('enrollments.end_date', 'End Date')}</p>
                    <p className="text-sm">{enrollment.end_date ? formatDateOnly(enrollment.end_date) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('enrollments.case_manager', 'Case Manager')}</p>
                    <p className="text-sm">{enrollment.assigned_to_name || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Goals (inlined into Overview — was a separate tab) */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('enrollments.goals', 'Goals')}</CardTitle>
                <Button size="sm" onClick={() => setGoalDialogOpen(true)}>
                  <GoalIcon className="h-4 w-4 mr-2" />
                  {t('enrollments.add_goal', 'Add Goal')}
                </Button>
              </CardHeader>
              <CardContent>
                {goals?.goals && goals.goals.length > 0 ? (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('enrollments.category', 'Category')}</TableHead>
                          <TableHead>{t('enrollments.goal', 'Goal')}</TableHead>
                          <TableHead>{t('enrollments.target_date', 'Target Date')}</TableHead>
                          <TableHead>{t('enrollments.status_3', 'Status')}</TableHead>
                          <TableHead>{t('enrollments.created_by', 'Created By')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {goals.goals.map((goal) => (
                          <TableRow key={goal.id}>
                            <TableCell>
                              <Badge variant="outline">{goal.goal_category}</Badge>
                            </TableCell>
                            <TableCell>{goal.goal_text}</TableCell>
                            <TableCell>{goal.target_date ? formatDateOnly(goal.target_date) : '-'}</TableCell>
                            <TableCell>
                              <Badge variant={goal.status === 'completed' ? 'default' : 'secondary'}>
                                {goal.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{goal.created_by_name || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    {t('enrollments.no_goals_set_yet', 'No goals set yet')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes (inlined into Overview — was a separate tab) */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('enrollments.notes_2', 'Notes')}</CardTitle>
                <Button size="sm" onClick={() => setNoteDialogOpen(true)}>
                  <NoteIcon className="h-4 w-4 mr-2" />
                  {t('enrollments.add_note', 'Add Note')}
                </Button>
              </CardHeader>
              <CardContent>
                {notes?.notes && notes.notes.length > 0 ? (
                  <div className="space-y-3">
                    {notes.notes.map((note) => (
                      <div key={note.id} className="border rounded-md p-3">
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant="outline">{note.note_type}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {note.created_ts ? formatDate.dateTime(note.created_ts, userTimezone) : '-'}
                          </span>
                        </div>
                        <p className="text-sm mb-2">{note.note_text}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">
                            By: {note.created_by_name || 'Unknown'}
                          </span>
                          {note.is_confidential && (
                            <Badge variant="destructive">{t('enrollments.confidential', 'Confidential')}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    {t('enrollments.no_notes_added_yet', 'No notes added yet')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Content - Activities */}
        <TabsContent value="activities" className="mt-6">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h2 className="text-xl font-semibold">{t('enrollments.activities_workflows', 'Activities & Workflows')}</h2>
            <Button onClick={() => setActivityDialogOpen(true)}>
              <AddIcon className="h-4 w-4 mr-2" />
              {t('enrollments.add_activity', 'Add Activity')}
            </Button>
          </div>

          {/* Workflow Progress Section */}
          {workflowInstances.length > 0 ? (
            <div className="mb-6">
              {workflowInstances.map((instance) => (
                <MemberWorkflowProgress
                  key={instance.id}
                  workflowInstanceId={instance.id}
                  enrollmentId={enrollmentId!}
                  onStepExecute={(stepId, action) => {
                    refetchWorkflowInstances();
                    activities.refetch?.();
                  }}
                  // Legacy callbacks for backward compatibility
                  onNavigateToCommunication={(channel) => {
                    setSelectedCommunicationMethod(channel);
                    setActiveTab('conversations');
                  }}
                  onNavigateToForm={(formType) => {
                    setActiveTab('forms');
                  }}
                  // Enhanced context-aware navigation callbacks
                  onNavigateToFormWithContext={(context: FormNavigationContext) => {
                    setWorkflowNavContext({
                      workflowInstanceId: context.workflowInstanceId,
                      workflowStepId: context.workflowStepId,
                      workflowStepInstanceId: context.workflowStepInstanceId,
                      stepName: context.stepName,
                      returnTab: 'activities',
                      formTemplateId: context.formTemplateId,
                    });
                    setActiveTab('forms');
                  }}
                  onNavigateToCommunicationWithContext={(context: CommunicationNavigationContext) => {
                    setWorkflowNavContext({
                      workflowInstanceId: context.workflowInstanceId,
                      workflowStepId: context.workflowStepId,
                      workflowStepInstanceId: context.workflowStepInstanceId,
                      stepName: context.stepName,
                      returnTab: 'activities',
                      communicationTemplateId: context.templateId,
                    });
                    setSelectedCommunicationMethod(context.channel);
                    setActiveTab('conversations');
                  }}
                  onNavigateToDocuments={(context: DocumentNavigationContext) => {
                    setWorkflowNavContext({
                      workflowInstanceId: context.workflowInstanceId,
                      workflowStepId: context.workflowStepId,
                      workflowStepInstanceId: context.workflowStepInstanceId,
                      stepName: context.stepName,
                      returnTab: 'activities',
                    });
                    setActiveTab('documents');
                    setDocumentDialogOpen(true);
                  }}
                  onAddActivity={() => setActivityDialogOpen(true)}
                />
              ))}
            </div>
          ) : workflows.length > 0 || availableWorkflowTemplates.length > 0 ? (
            <Card className="mb-6 border-dashed">
              <CardContent className="py-6">
                <div className="text-center">
                  <NextStepIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <h3 className="font-semibold mb-2">{t('enrollments.no_workflow_started', 'No Workflow Started')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('enrollments.start_workflow_description', 'Start a workflow to track this member\'s progress through structured steps.')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {(availableWorkflowTemplates.length > 0 ? availableWorkflowTemplates : workflows).map((template) => (
                      <Button
                        key={template.id}
                        variant="outline"
                        onClick={async () => {
                          const result = await createWorkflowInstance(template.id);
                          if (result) {
                            refetchWorkflowInstances();
                          }
                        }}
                      >
                        <AddIcon className="h-4 w-4 mr-2" />
                        Start: {template.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert className="mb-4">
              <AlertDescription>
                {t('enrollments.no_workflows_are_associated_with', 'No workflows are associated with this program. Create a workflow in the program settings to track structured activities.')}
              </AlertDescription>
            </Alert>
          )}

          {/* Member activity feed (TRUS-420).
              Replaces the old Activity Log table, whose Notes column was
              `max-w-xs truncate` — a DCM could not read a note on the page at
              all. This shows every note in full, newest first, with its author,
              timestamp and tags, and merges the member/workflow/activity
              updates into the same list so the screen tells the whole story of
              the case rather than only what someone logged by hand. */}
          {enrollment?.member_id && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-3">{t('enrollments.activity_log', 'Activity Log')}</h3>
              <MemberActivityFeed
                memberId={enrollment.member_id}
                enrollmentId={enrollment.id}
              />
            </div>
          )}

          {/* No separate empty state here any more. It keyed off
              `activities.activities`, so it rendered "No activities recorded yet"
              directly beneath a populated feed whenever a member had notes but no
              enrollment_activities rows. MemberActivityFeed owns its own empty
              state, across every source it merges. */}
        </TabsContent>

        {/* Tab Content - Documents */}
        <TabsContent value="documents" className="mt-6">
          {/* Full-page Request Signature flow */}
          {signatureRequestOpen ? (
            <RequestSignaturePage
              onBack={() => { setSignatureRequestOpen(false); documents.fetchDocuments(); }}
              recipientName={enrollment?.member_name || (member ? `${member.first_name} ${member.last_name}` : '')}
              recipientEmail={member?.email || ''}
              recipientPhone={member?.phone_no || member?.phone || ''}
              recipientMemberId={enrollment?.member_id}
              defaultTitle={`Signature Request - ${enrollment?.program_name || 'Enrollment'}`}
              defaultMessage="Please review and sign the required documents for your enrollment."
            />
          ) : (
          <>
          {/* Return to Workflow Banner */}
          {workflowNavContext && activeTab === 'documents' && (
            <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <AlertDescription className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DocumentIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-800 dark:text-blue-200">
                    Working on workflow step: <strong>{workflowNavContext.stepName}</strong>
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    setActiveTab(workflowNavContext.returnTab);
                    setWorkflowNavContext(null);
                  }}
                >
                  <BackIcon className="h-4 w-4 mr-1" />
                  Return to Workflow
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {/* Document Requirements Checklist */}
          {enrollmentId && (
            <DocumentRequirements
              context="enrollment"
              contextId={enrollmentId}
              onUploadForType={() => setDocumentDialogOpen(true)}
            />
          )}

          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h2 className="text-xl font-semibold">{t('enrollments.documents', 'Documents')}</h2>
            <div className="flex gap-2">
              <Button onClick={() => setDocumentDialogOpen(true)}>
                <DocumentIcon className="h-4 w-4 mr-2" />
                {t('enrollments.upload_document', 'Upload Document')}
              </Button>
              <Button variant="outline" onClick={() => setSignatureRequestOpen(true)}>
                <PenToolIcon className="h-4 w-4 mr-2" />
                Request Signature
              </Button>
            </div>
          </div>

          {documents?.documents && documents.documents.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('enrollments.document_type', 'Document Type')}</TableHead>
                    <TableHead>{t('enrollments.file_name', 'File Name')}</TableHead>
                    <TableHead>{t('enrollments.notes', 'Notes')}</TableHead>
                    <TableHead>{t('enrollments.upload_date', 'Upload Date')}</TableHead>
                    <TableHead>{t('enrollments.uploaded_by', 'Uploaded By')}</TableHead>
                    <TableHead>{t('enrollments.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.documents.map((doc) => {
                    const displayName = doc.file_name || doc.document_name || doc.file_path?.split('/').pop() || 'Untitled';
                    const isImage = doc.mime_type?.startsWith('image/') || doc.document_type === 'mms_image';
                    const viewUrl = doc.file_url || (doc.file_path ? `/api/document-processing/files/${doc.file_path}` : null);

                    return (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Badge variant={doc.document_type?.startsWith('mms_') ? 'secondary' : 'outline'}>
                            {doc.document_type?.startsWith('mms_')
                              ? `MMS ${doc.document_type.replace('mms_', '').charAt(0).toUpperCase() + doc.document_type.replace('mms_', '').slice(1)}`
                              : doc.document_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isImage && <ImageIcon className="h-4 w-4 text-muted-foreground" />}
                            {displayName}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[220px]">
                          {doc.notes ? (
                            <span className="text-sm text-muted-foreground whitespace-pre-wrap break-words" title={doc.notes}>
                              {doc.notes}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>{doc.upload_date ? formatDate.short(doc.upload_date, userTimezone) : '-'}</TableCell>
                        <TableCell>{doc.uploaded_by_name || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {viewUrl && (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="View document"
                                onClick={async () => {
                                  const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token') || '';
                                  const cboId = localStorage.getItem('active_cbo_id') || '';
                                  const baseUrl = (config.weaveinApi.baseUrl ?? '').replace(/\/$/, '');

                                  // New doc system URLs use /api/documents/ prefix
                                  if (viewUrl.startsWith('/api/documents/')) {
                                    try {
                                      const res = await fetch(`${baseUrl}${viewUrl}`, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'X-CBO-ID': cboId,
                                        },
                                      });
                                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                      const blob = await res.blob();
                                      const blobUrl = URL.createObjectURL(blob);

                                      // Check if browser can render this type inline
                                      const mime = (doc.mime_type || '').toLowerCase();
                                      const viewableInline = mime.startsWith('image/') || mime === 'application/pdf' || mime === 'text/plain';

                                      if (viewableInline) {
                                        window.open(blobUrl, '_blank');
                                      } else {
                                        // Force download for DOCX, XLSX, PPTX, etc.
                                        const a = document.createElement('a');
                                        a.href = blobUrl;
                                        a.download = displayName;
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                      }
                                    } catch (e) {
                                      console.error('View error:', e);
                                    }
                                  } else {
                                    // Legacy document URL — open in new tab
                                    window.open(`${baseUrl}${viewUrl}?token=${encodeURIComponent(token)}`, '_blank');
                                  }
                                }}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            )}
                            {confirmDeleteDocId === doc.id ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    documents.deleteDocument(doc.id);
                                    setConfirmDeleteDocId(null);
                                  }}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setConfirmDeleteDocId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Delete document"
                                onClick={() => setConfirmDeleteDocId(doc.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <DocumentIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('enrollments.no_documents_uploaded_yet', 'No documents uploaded yet')}</p>
            </div>
          )}
          </>
          )}
        </TabsContent>

        {/* Tab Content - Tasks */}
        <TabsContent value="tasks" className="mt-6">
          {enrollment && member && (
            <TaskList
              memberId={enrollment.member_id}
              memberName={`${member.first_name} ${member.last_name}`}
            />
          )}
        </TabsContent>

        {/* Tab Content - Conversations & Outreach */}
        <TabsContent value="conversations" className="mt-6">
          {/* Outreach & engagement documentation (MCP Outreach program), collapsible:
              pick engagement type + channel (SMS/email/voice) + template, record a
              progress note with timing, set disposition — for tracking & billing. */}
          {enrollment?.program_name === 'MCP Outreach & Engagement' && enrollment?.member_id && (
            <div className="mb-6">
              <CollapsibleCard id="outreach-section" title="Outreach & engagement" defaultOpen>
                <OutreachEncounterPanel memberId={enrollment.member_id} />
              </CollapsibleCard>
            </div>
          )}

          <CollapsibleCard id="conversations-section" title="Conversations" defaultOpen>
          {/* Return to Workflow Banner */}
          {workflowNavContext && activeTab === 'conversations' && (
            <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <AlertDescription className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <SmsIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-800 dark:text-blue-200">
                    Working on workflow step: <strong>{workflowNavContext.stepName}</strong>
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    setActiveTab(workflowNavContext.returnTab);
                    setWorkflowNavContext(null);
                  }}
                >
                  <BackIcon className="h-4 w-4 mr-1" />
                  Return to Workflow
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <div>
            <h2 className="text-xl font-semibold mb-2">{t('enrollments.conversations', 'Conversations')}</h2>
            <p className="text-sm text-muted-foreground mb-6">
              View and initiate 1-1 communications with {member?.first_name || 'the member'}
            </p>

            {/* Communication Method Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Button
                variant={selectedCommunicationMethod === 'email' ? 'default' : 'outline'}
                className="h-auto py-4 justify-start"
                onClick={() => {
                  setSelectedCommunicationMethod('email');
                  setShowMessageComposer(true);
                }}
                disabled={!member?.email}
              >
                <EmailIcon className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-medium">{t('enrollments.email_1', 'Email')}</div>
                  <div className="text-xs opacity-70">
                    {member?.email || 'No email on file'}
                  </div>
                </div>
              </Button>
              <Button
                variant={selectedCommunicationMethod === 'sms' ? 'default' : 'outline'}
                className="h-auto py-4 justify-start"
                onClick={() => {
                  setSelectedCommunicationMethod('sms');
                  setShowMessageComposer(true);
                }}
                disabled={!member?.phone_no && !member?.phone_number}
              >
                <SmsIcon className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-medium">SMS</div>
                  <div className="text-xs opacity-70">
                    {member?.phone_no || member?.phone_number || 'No phone on file'}
                  </div>
                </div>
              </Button>
              <Button
                variant={selectedCommunicationMethod === 'phone' ? 'default' : 'outline'}
                className="h-auto py-4 justify-start"
                onClick={() => {
                  setSelectedCommunicationMethod('phone');
                }}
                disabled={!member?.phone_no && !member?.phone_number}
              >
                <PhoneIcon className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <div className="font-medium">{t('enrollments.phone_call', 'Phone Call')}</div>
                  <div className="text-xs opacity-70">
                    {member?.phone_no || member?.phone_number || 'No phone on file'}
                  </div>
                </div>
              </Button>
            </div>

            {/* Phone Interface */}
            {selectedCommunicationMethod === 'phone' && member && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <MemberPhoneInterface
                    member={member}
                    enrollmentId={enrollmentId}
                  />
                </CardContent>
              </Card>
            )}

            {/* Message Composer — shared with the member activity view (TRUS-762). */}
            {showMessageComposer && member && (selectedCommunicationMethod === 'email' || selectedCommunicationMethod === 'sms') && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <MemberMessageComposer
                    key={selectedCommunicationMethod}
                    member={member}
                    channel={selectedCommunicationMethod}
                    enrollmentId={enrollmentId}
                    programId={enrollment?.program_id ?? null}
                    onCancel={() => setShowMessageComposer(false)}
                    onSent={() => {
                      setShowMessageComposer(false);
                      fetchSentMessages();
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Threaded Conversation History */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold mb-1">{t('enrollments.conversation_history', 'Conversation History')}</h3>
                  <p className="text-sm text-muted-foreground">
                    All messages with {member?.first_name || 'the member'}
                    {responsesUnreadCount > 0 && (
                      <Badge variant="destructive" className="ml-2">{responsesUnreadCount} unread</Badge>
                    )}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { fetchSentMessages(); refetchResponses(); }}>
                  {t('enrollments.refresh', 'Refresh')}
                </Button>
              </div>

              {(sentMessagesLoading || responsesLoading) ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (sentMessages.length === 0 && communicationResponses.length === 0) ? (
                <Alert>
                  <AlertDescription>
                    {t('enrollments.no_conversation_history_yet_use', 'No conversation history yet. Use the buttons above to send SMS or email.')}
                  </AlertDescription>
                </Alert>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {[
                    ...sentMessages.map(msg => ({
                      id: `sent-${msg.id}`,
                      // Use the stored direction as-is. (Previously inverted to work around
                      // a since-fixed backend bug where staff sends were mislabeled 'inbound';
                      // the flip now double-inverts and shows received messages as 'Sent'.)
                      direction: (msg.direction === 'inbound' ? 'inbound' : 'outbound') as 'inbound' | 'outbound',
                      type: msg.communication_type,
                      subject: msg.subject,
                      message: msg.message_body,
                      status: msg.status,
                      timestampRaw: msg.created_at,
                      timestamp: new Date(msg.created_at),
                      isRead: true,
                      followUpRequired: false,
                      originalData: msg
                    })),
                    ...communicationResponses.map(resp => ({
                      id: `response-${resp.id}`,
                      direction: 'inbound' as const,
                      type: resp.response_channel?.toLowerCase() || 'sms',
                      subject: null,
                      message: resp.response_content,
                      status: resp.is_read ? 'read' : 'unread',
                      timestampRaw: resp.response_timestamp,
                      timestamp: new Date(resp.response_timestamp),
                      isRead: resp.is_read,
                      followUpRequired: resp.follow_up_required,
                      originalData: resp
                    }))
                  ]
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                    .map((item) => (
                      <AccordionItem key={item.id} value={item.id}>
                        <AccordionTrigger>
                          <div className="flex items-center gap-2 w-full pr-4">
                            {/* Direction indicator */}
                            <Badge
                              variant={item.direction === 'outbound' ? 'outline' : 'default'}
                            >
                              {item.type === 'email' ? <EmailIcon className="h-3 w-3 mr-1" /> : item.type === 'phone' ? <PhoneIcon className="h-3 w-3 mr-1" /> : <SmsIcon className="h-3 w-3 mr-1" />}
                              {item.direction === 'outbound' ? 'Sent' : 'Received'}
                            </Badge>

                            {/* Message preview */}
                            <div className="flex-1 min-w-0 text-left">
                              {item.subject && (
                                <p className="font-medium text-sm truncate">
                                  {item.subject}
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground truncate">
                                {item.message?.includes('[Attachment:') && (
                                  <ImageIcon className="h-3 w-3 inline mr-1" />
                                )}
                                {item.message?.split('\n').filter((l: string) => !l.startsWith('[Attachment:')).join(' ').substring(0, 60)}
                                {item.message && item.message.length > 60 ? '...' : ''}
                              </p>
                            </div>

                            {/* Status indicators */}
                            <div className="flex items-center gap-2 shrink-0">
                              {!item.isRead && item.direction === 'inbound' && (
                                <Badge variant="destructive">{t('enrollments.new', 'New')}</Badge>
                              )}
                              {item.followUpRequired && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <GoalIcon className="h-4 w-4 text-yellow-600" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {t('enrollments.followup_required', 'Follow-up required')}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatDate.dateTime(item.timestampRaw || item.timestamp, userTimezone)}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div>
                            {/* Full message content with attachment support */}
                            <div className="text-sm whitespace-pre-wrap mb-4">
                              {item.message?.split('\n').map((line: string, lineIdx: number) => {
                                // Detect markdown-style attachment links: [Attachment: filename](/api/...)
                                const attachMatch = line.match(/^\[Attachment:\s*(.+?)\]\((.+?)\)$/);
                                if (attachMatch) {
                                  const fileName = attachMatch[1];
                                  const fileUrl = attachMatch[2];
                                  const isImage = /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(fileName);
                                  const token = typeof window !== 'undefined' ? (localStorage.getItem('auth_token') || localStorage.getItem('member_token') || '') : '';
                                  const baseUrl = typeof window !== 'undefined' ? (config.weaveinApi.baseUrl ?? '').replace(/\/$/, '') : '';
                                  const fullUrl = `${baseUrl}${fileUrl}?token=${encodeURIComponent(token)}`;

                                  return (
                                    <div key={lineIdx} className="my-2">
                                      {isImage && (
                                        <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                                          <img
                                            src={fullUrl}
                                            alt={fileName}
                                            className="max-w-xs max-h-48 rounded border cursor-pointer hover:opacity-80 mb-1"
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                          />
                                        </a>
                                      )}
                                      <a
                                        href={fullUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-xs"
                                      >
                                        <DownloadIcon className="h-3 w-3" />
                                        {fileName}
                                      </a>
                                    </div>
                                  );
                                }
                                return <span key={lineIdx}>{line}{'\n'}</span>;
                              })}
                            </div>

                            {/* Actions for inbound messages */}
                            {item.direction === 'inbound' && (
                              <div className="flex gap-2 pt-4 border-t">
                                {!item.isRead && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => markResponseAsRead(item.originalData.id)}
                                  >
                                    {t('enrollments.mark_as_read', 'Mark as Read')}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant={item.followUpRequired ? 'default' : 'outline'}
                                  onClick={() => updateResponseFollowUp(
                                    item.originalData.id,
                                    !item.followUpRequired
                                  )}
                                >
                                  <GoalIcon className="h-4 w-4 mr-2" />
                                  {item.followUpRequired ? 'Remove Follow-up' : 'Flag for Follow-up'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))
                  }
                </Accordion>
              )}
            </div>
          </div>
          </CollapsibleCard>
        </TabsContent>

        {/* Tab Content - Forms */}
        <TabsContent value="forms" className="mt-6">
          {/* Return to Workflow Banner */}
          {workflowNavContext && activeTab === 'forms' && (
            <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <AlertDescription className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <DocumentIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-blue-800 dark:text-blue-200">
                    Working on workflow step: <strong>{workflowNavContext.stepName}</strong>
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    setActiveTab(workflowNavContext.returnTab);
                    setWorkflowNavContext(null);
                  }}
                >
                  <BackIcon className="h-4 w-4 mr-1" />
                  Return to Workflow
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <FormLibraryNew
            enrollmentId={enrollmentId}
            memberId={enrollment?.member_id}
            programId={enrollment?.program_id}
            organizationId={parseInt(localStorage.getItem('active_cbo_id') || '0') || undefined}
            workflowStepId={workflowNavContext?.workflowStepId}
            workflowInstanceId={workflowNavContext?.workflowInstanceId}
            formTemplateId={workflowNavContext?.formTemplateId}
            onReturnToWorkflow={() => {
              setActiveTab(workflowNavContext?.returnTab || 'activities');
              setWorkflowNavContext(null);
            }}
          />
        </TabsContent>

        {/* History tab removed — change history is now unified on the Members page */}
      </Tabs>

      {/* Dialogs */}

      {/* Edit Enrollment Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('enrollments.edit_enrollment_1', 'Edit Enrollment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-status">{t('enrollments.status_4', 'Status')}</Label>
              <Select
                value={editFormData.enrollment_status}
                onValueChange={(value) => setEditFormData({ ...editFormData, enrollment_status: value })}
              >
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('enrollments.pending', 'Pending')}</SelectItem>
                  <SelectItem value="active">{t('enrollments.active', 'Active')}</SelectItem>
                  <SelectItem value="on_hold">{t('enrollments.on_hold', 'On Hold')}</SelectItem>
                  <SelectItem value="completed">{t('enrollments.completed', 'Completed')}</SelectItem>
                  <SelectItem value="cancelled">{t('enrollments.cancelled', 'Cancelled')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-priority">{t('enrollments.priority_1', 'Priority')}</Label>
              <Select
                value={editFormData.enrollment_priority}
                onValueChange={(value) => setEditFormData({ ...editFormData, enrollment_priority: value })}
              >
                <SelectTrigger id="edit-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('enrollments.low', 'Low')}</SelectItem>
                  <SelectItem value="medium">{t('enrollments.medium', 'Medium')}</SelectItem>
                  <SelectItem value="high">{t('enrollments.high', 'High')}</SelectItem>
                  <SelectItem value="urgent">{t('enrollments.urgent', 'Urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-case-manager">{t('enrollments.case_manager_1', 'Case Manager')}</Label>
              <Select
                value={editFormData.assigned_to || '__none__'}
                onValueChange={(value) => setEditFormData({ ...editFormData, assigned_to: value === '__none__' ? '' : value })}
              >
                <SelectTrigger id="edit-case-manager">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {availableUsers.map((user: any) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.name || user.email}{user.role_names ? ` — ${user.role_names}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-enrollment-date">{t('enrollments.enrollment_date_1', 'Enrollment Date')}</Label>
              <Input
                id="edit-enrollment-date"
                type="date"
                value={editFormData.enrollment_date}
                onChange={(e) => setEditFormData({ ...editFormData, enrollment_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-start-date">{t('enrollments.start_date_1', 'Start Date')}</Label>
              <Input
                id="edit-start-date"
                type="date"
                value={editFormData.start_date}
                onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-end-date">{t('enrollments.end_date_1', 'End Date')}</Label>
              <Input
                id="edit-end-date"
                type="date"
                value={editFormData.end_date}
                onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="edit-notes">Notes</Label>
              <textarea
                id="edit-notes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>{t('enrollments.cancel_2', 'Cancel')}</Button>
            <Button onClick={handleSaveEnrollment}>{t('enrollments.save_changes', 'Save Changes')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('enrollments.add_activity_1', 'Add Activity')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="activity-type">{t('enrollments.activity_type', 'Activity Type')}</Label>
              <Select
                value={activityData.activity_type}
                onValueChange={(value) => setActivityData({ ...activityData, activity_type: value })}
              >
                <SelectTrigger id="activity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outreach">{t('enrollments.outreach', 'Outreach')}</SelectItem>
                  <SelectItem value="assessment">{t('enrollments.assessment', 'Assessment')}</SelectItem>
                  <SelectItem value="care_plan">{t('enrollments.care_plan', 'Care Plan')}</SelectItem>
                  <SelectItem value="follow_up">{t('enrollments.followup', 'Follow-up')}</SelectItem>
                  <SelectItem value="case_management">{t('enrollments.case_management', 'Case Management')}</SelectItem>
                  <SelectItem value="referral">{t('enrollments.referral', 'Referral')}</SelectItem>
                  <SelectItem value="other">{t('enrollments.other', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="activity-date">{t('enrollments.activity_date', 'Activity Date')}</Label>
              <Input
                id="activity-date"
                type="date"
                value={activityData.activity_date}
                onChange={(e) => setActivityData({ ...activityData, activity_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="activity-status">{t('enrollments.status_5', 'Status')}</Label>
              <Select
                value={activityData.status}
                onValueChange={(value) => setActivityData({ ...activityData, status: value })}
              >
                <SelectTrigger id="activity-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">{t('enrollments.scheduled', 'Scheduled')}</SelectItem>
                  <SelectItem value="completed">{t('enrollments.completed_1', 'Completed')}</SelectItem>
                  <SelectItem value="cancelled">{t('enrollments.cancelled_1', 'Cancelled')}</SelectItem>
                  <SelectItem value="no_show">{t('enrollments.no_show', 'No Show')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="activity-notes">Notes</Label>
              <textarea
                id="activity-notes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={activityData.notes || ''}
                onChange={(e) => setActivityData({ ...activityData, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>{t('enrollments.cancel_3', 'Cancel')}</Button>
            <Button onClick={handleCreateActivity}>{t('enrollments.save_activity', 'Save Activity')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog - New configurable document system */}
      <DocumentUploadDialog
        open={documentDialogOpen}
        onOpenChange={(open) => {
          setDocumentDialogOpen(open);
          if (!open) {
            setSelectedFile(null);
            // Always refresh documents when dialog closes (catches uploads that completed)
            documents.fetchDocuments();
          }
        }}
        targetType="member"
        targetId={enrollment?.member_id || 0}
        targetName={enrollment?.member_name || member?.first_name ? `${member?.first_name} ${member?.last_name}` : undefined}
        defaultEnrollmentId={enrollmentId || undefined}
        defaultProgramId={enrollment?.program_id || undefined}
        onUploadComplete={() => { documents.fetchDocuments(); }}
      />

      {/* Request Signature — removed modal, using inline page in documents tab */}

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('enrollments.add_note_1', 'Add Note')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="note-type">{t('enrollments.note_type', 'Note Type')}</Label>
              <Select
                value={noteData.note_type}
                onValueChange={(value) => setNoteData({ ...noteData, note_type: value })}
              >
                <SelectTrigger id="note-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t('enrollments.general', 'General')}</SelectItem>
                  <SelectItem value="clinical">{t('enrollments.clinical', 'Clinical')}</SelectItem>
                  <SelectItem value="administrative">{t('enrollments.administrative', 'Administrative')}</SelectItem>
                  <SelectItem value="billing">{t('enrollments.billing', 'Billing')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="note-text">Note Text</Label>
              <textarea
                id="note-text"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={noteData.note_text}
                onChange={(e) => setNoteData({ ...noteData, note_text: e.target.value })}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>{t('enrollments.cancel_5', 'Cancel')}</Button>
            <Button onClick={handleCreateNote}>{t('enrollments.save_note', 'Save Note')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('enrollments.add_goal_1', 'Add Goal')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="goal-category">{t('enrollments.goal_category', 'Goal Category')}</Label>
              <Select
                value={goalData.goal_category}
                onValueChange={(value) => setGoalData({ ...goalData, goal_category: value })}
              >
                <SelectTrigger id="goal-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="health">{t('enrollments.health', 'Health')}</SelectItem>
                  <SelectItem value="housing">{t('enrollments.housing', 'Housing')}</SelectItem>
                  <SelectItem value="employment">{t('enrollments.employment', 'Employment')}</SelectItem>
                  <SelectItem value="education">{t('enrollments.education', 'Education')}</SelectItem>
                  <SelectItem value="social">{t('enrollments.social', 'Social')}</SelectItem>
                  <SelectItem value="other">{t('enrollments.other_1', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="goal-text">Goal Description</Label>
              <textarea
                id="goal-text"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={goalData.goal_text}
                onChange={(e) => setGoalData({ ...goalData, goal_text: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="goal-target-date">{t('enrollments.target_date_1', 'Target Date')}</Label>
              <Input
                id="goal-target-date"
                type="date"
                value={goalData.target_date || ''}
                onChange={(e) => setGoalData({ ...goalData, target_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>{t('enrollments.cancel_6', 'Cancel')}</Button>
            <Button onClick={handleCreateGoal}>{t('enrollments.save_goal', 'Save Goal')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnrollmentDetailNew;
